/**
 * ProgressBar Component
 * 
 * Reusable progress bar with optional label and color variants.
 */

import React from 'react';

export type ProgressBarSize = 'sm' | 'md' | 'lg';

export interface ProgressBarProps {
  value: number;
  max?: number;
  showLabel?: boolean;
  showPercentage?: boolean;
  size?: ProgressBarSize;
  colorByValue?: boolean;
  className?: string;
}

const sizeClasses: Record<ProgressBarSize, string> = {
  sm: 'h-1.5',
  md: 'h-2.5',
  lg: 'h-4',
};

const getColorByValue = (percentage: number): string => {
  if (percentage >= 80) return 'bg-green-500';
  if (percentage >= 50) return 'bg-amber-500';
  return 'bg-red-500';
};

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  showLabel = false,
  showPercentage = false,
  size = 'md',
  colorByValue = true,
  className = '',
}) => {
  const percentage = Math.min(Math.round((value / max) * 100), 100);
  const barColor = colorByValue ? getColorByValue(percentage) : 'bg-[#c00035]';

  return (
    <div className={`w-full ${className}`}>
      {(showLabel || showPercentage) && (
        <div className="flex justify-between items-center mb-1">
          {showLabel && (
            <span className="text-xs text-gray-500">
              {value} / {max}
            </span>
          )}
          {showPercentage && (
            <span className={`text-xs font-medium ${percentage >= 80 ? 'text-green-600' :
                percentage >= 50 ? 'text-amber-600' : 'text-[#c00035]'
              }`}>
              {percentage}%
            </span>
          )}
        </div>
      )}

      <div className={`w-full bg-gray-200 rounded-full overflow-hidden ${sizeClasses[size]}`}>
        <div
          className={`${barColor} ${sizeClasses[size]} rounded-full transition-all duration-500 ease-out`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

export default ProgressBar;

